import React from 'react';
import { Link } from 'react-router-dom';
import { Truck, MapPin, CreditCard, MessageCircle, ArrowRight, Clock, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import Layout from '../layout/Layout';
import PageHeader from '../layout/PageHeader';
import { businessInfo } from '../../data/mockData';
import { getDefaultInquiryURL } from '../../utils/whatsappGenerator';
import { actionClasses } from '../../config/designSystem';

const DeliveryPage = () => {
  return (
    <Layout>
      <PageHeader
        title="Delivery & Payment"
        description="Fast delivery across Los Angeles. Simple payment via Zelle or Apple Cash."
        videoSrc="/images/delevery_animated.mp4"
      />

      {/* Delivery Info */}
      <section className="relative overflow-hidden bg-[#F7F2EE] py-16 md:py-20">
        <div className="pointer-events-none absolute -right-28 top-20 h-72 w-72 rounded-full bg-rose-200/30 blur-3xl" />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            {/* Delivery */}
            <Card className="rounded-[1.5rem] border border-rose-200/70 bg-white/68 shadow-[0_18px_55px_rgba(120,72,54,0.10)] backdrop-blur-xl">
              <CardContent className="p-8">
                <div className="w-14 h-14 rounded-2xl border border-rose-200/80 bg-gradient-to-br from-white/90 to-rose-100/70 flex items-center justify-center mb-6 shadow-[0_10px_28px_rgba(251,113,133,0.14)]">
                  <Truck className="w-7 h-7 text-[#FB7185]" />
                </div>
                <h2 className="font-serif text-2xl font-semibold text-[#3A2A2A] mb-4">
                  Delivery
                </h2>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3 text-stone-600">
                    <Check className="w-5 h-5 text-[#FB7185] flex-shrink-0 mt-0.5" />
                    <span>Service across <strong className="text-[#3A2A2A]">Los Angeles & LA County</strong></span>
                  </li>
                  <li className="flex items-start gap-3 text-stone-600">
                    <Check className="w-5 h-5 text-[#FB7185] flex-shrink-0 mt-0.5" />
                    <span>Delivery from <strong className="text-[#3A2A2A]">$10</strong> (depends on distance)</span>
                  </li>
                  <li className="flex items-start gap-3 text-stone-600">
                    <Check className="w-5 h-5 text-[#FB7185] flex-shrink-0 mt-0.5" />
                    <span>Based in <strong className="text-[#3A2A2A]">Glendale, California</strong></span>
                  </li>
                  <li className="flex items-start gap-3 text-stone-600">
                    <Check className="w-5 h-5 text-[#FB7185] flex-shrink-0 mt-0.5" />
                    <span>Same-day delivery available (contact us)</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Pickup */}
            <Card className="rounded-[1.5rem] border border-rose-200/70 bg-white/68 shadow-[0_18px_55px_rgba(120,72,54,0.10)] backdrop-blur-xl">
              <CardContent className="p-8">
                <div className="w-14 h-14 rounded-2xl border border-rose-200/80 bg-gradient-to-br from-white/90 to-rose-100/70 flex items-center justify-center mb-6 shadow-[0_10px_28px_rgba(251,113,133,0.14)]">
                  <MapPin className="w-7 h-7 text-[#FB7185]" />
                </div>
                <h2 className="font-serif text-2xl font-semibold text-[#3A2A2A] mb-4">
                  Pickup
                </h2>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3 text-stone-600">
                    <Check className="w-5 h-5 text-[#FB7185] flex-shrink-0 mt-0.5" />
                    <span>Free pickup from <strong className="text-[#3A2A2A]">Glendale</strong></span>
                  </li>
                  <li className="flex items-start gap-3 text-stone-600">
                    <Check className="w-5 h-5 text-[#FB7185] flex-shrink-0 mt-0.5" />
                    <span>Schedule your pickup time via WhatsApp</span>
                  </li>
                  <li className="flex items-start gap-3 text-stone-600">
                    <Check className="w-5 h-5 text-[#FB7185] flex-shrink-0 mt-0.5" />
                    <span>No delivery fee — <strong className="text-[#3A2A2A]">$0</strong></span>
                  </li>
                  <li className="flex items-start gap-3 text-stone-600">
                    <Check className="w-5 h-5 text-[#FB7185] flex-shrink-0 mt-0.5" />
                    <span>Exact address provided after confirmation</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Payment */}
          <div className="max-w-3xl mx-auto">
            <Card className="rounded-[1.5rem] border border-rose-200/70 bg-white/68 shadow-[0_18px_55px_rgba(120,72,54,0.10)] backdrop-blur-xl">
              <CardContent className="p-8">
                <div className="text-center mb-6">
                  <div className="w-14 h-14 rounded-2xl border border-rose-200/80 bg-gradient-to-br from-white/90 to-rose-100/70 flex items-center justify-center mx-auto mb-4 shadow-[0_10px_28px_rgba(251,113,133,0.14)]">
                    <CreditCard className="w-7 h-7 text-[#FB7185]" />
                  </div>
                  <h2 className="font-serif text-2xl font-semibold text-[#3A2A2A] mb-2">
                    Payment Methods
                  </h2>
                </div>

                <div className="rounded-xl border border-rose-200/60 bg-white/72 p-6 mb-6 shadow-inner shadow-rose-100/50">
                  <div className="flex flex-wrap items-center justify-center gap-6 mb-4">
                    <div className="flex items-center gap-2 px-4 py-2 bg-white/85 border border-rose-200/60 rounded-lg shadow-sm">
                      <span className="text-lg font-semibold text-blue-600">Zelle</span>
                    </div>
                    <div className="flex items-center gap-2 px-4 py-2 bg-white/85 border border-rose-200/60 rounded-lg shadow-sm">
                      <span className="text-lg font-semibold text-[#3A2A2A]">Apple Cash</span>
                    </div>
                  </div>
                  <p className="text-center text-stone-600">
                    Payment details are confirmed via WhatsApp for convenience and security.
                  </p>
                </div>

                <p className="text-sm text-stone-600 text-center">
                  We'll send you payment instructions after confirming your order details.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Hours */}
      <section className="relative z-[1] overflow-visible bg-[#FFF8F5] py-16">
        <div className="section-divider-rose absolute top-0 left-0 right-0" aria-hidden="true" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-3 mb-8">
            <Clock className="w-6 h-6 text-[#FB7185]" />
            <h2 className="font-serif text-2xl font-semibold text-[#3A2A2A]">Working Hours</h2>
          </div>
          <div className="text-center">
            <p className="text-xl text-[#3A2A2A] font-medium mb-2">{businessInfo.workingHours}</p>
            <p className="text-stone-600">Fast WhatsApp response • For urgent orders, message us directly</p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative z-[2] isolate overflow-visible bg-[#F7F2EE] py-16 md:py-20">
        <div className="section-divider-rose absolute top-0 left-0 right-0 z-[30]" aria-hidden="true" />
        <div className="pointer-events-none absolute bottom-[-260px] right-0 z-[1] hidden md:block" aria-hidden="true">
          <img
            src="/images/del_flow.png"
            alt=""
            className="h-[760px] w-auto max-w-none object-contain object-bottom-right lg:h-[820px]"
          />
        </div>
        <div className="relative z-[20] max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl md:text-4xl font-semibold text-[#3A2A2A] mb-4">
            Ready to Order?
          </h2>
          <p className="text-stone-600 mb-8 max-w-xl mx-auto">
            Build your bouquet online or chat with us directly on WhatsApp
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/build">
              <Button size="lg" className={`w-full sm:w-auto px-8 ${actionClasses.primary}`}>
                Build Your Bouquet
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <a href={getDefaultInquiryURL()} target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="outline" className="w-full sm:w-auto px-8 border-[#FB7185]/30 bg-white/62 text-[#3A2A2A] hover:bg-white/85 hover:text-[#FB7185] shadow-[0_14px_40px_rgba(120,72,54,0.10)] backdrop-blur-xl">
                <MessageCircle className="w-5 h-5 mr-2" />
                WhatsApp
              </Button>
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default DeliveryPage;
